<script setup lang="ts">
  import type { ChatMessage } from '@/composables/useChatStream'
  import ChatStreamingLoader from '@/components/app/chat/messages/ChatStreamingLoader.vue'
  import SaveSnippetDialog from '@/components/app/chat/components/SaveSnippetDialog.vue'
  import SidePanelPreferenceSuggestion from '@/components/app/chat/sidepanel/SidePanelPreferenceSuggestion.vue'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useTranslation } from '@/composables/useTranslation'
  import { renderMarkdown } from '@/utils/renderMarkdown'
  import { FeatureEnum } from '@/types/generated'
  import { FeedbackService } from '@/types/generated/services.gen'
  import {
    ArrowDownToLine,
    Bookmark,
    Check,
    Copy,
    MoreHorizontal,
    ThumbsDown,
    ThumbsUp
  } from 'lucide-vue-next'
  import { computed, ref } from 'vue'

  const props = defineProps<{
    message: ChatMessage
    sessionUuid?: string
    /** Status label for the streaming loader (e.g. "Lese Dokumente..."). */
    streamingLabel?: string
  }>()

  const emit = defineEmits<{
    openPreferencesDialog: []
  }>()

  const preferenceSuggestion = computed(() => props.message.raw?.preference_suggestion ?? null)

  const { t } = useTranslation({
    en: {
      copied: 'Copied!',
      copy: 'Copy',
      thumbsUp: 'Thumbs up',
      thumbsDown: 'Thumbs down',
      moreOptions: 'More options',
      saveSnippet: 'Save snippet',
      download: 'Download'
    },
    de: {
      copied: 'Kopiert!',
      copy: 'Kopieren',
      thumbsUp: 'Daumen hoch',
      thumbsDown: 'Daumen runter',
      moreOptions: 'Weitere Optionen',
      saveSnippet: 'Snippet speichern',
      download: 'Herunterladen'
    },
    fr: {
      copied: 'Copié !',
      copy: 'Copier',
      thumbsUp: 'Pouce en haut',
      thumbsDown: 'Pouce en bas',
      moreOptions: "Plus d'options",
      saveSnippet: 'Enregistrer le snippet',
      download: 'Télécharger'
    }
  })

  const showCopied = ref(false)
  const rating = ref<'positive' | 'negative' | null>(null)
  const morePopoverOpen = ref(false)
  const showSnippetDialog = ref(false)

  const renderedHtml = computed(() => {
    if (!props.message.content) return ''
    return renderMarkdown(props.message.content)
  })

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(props.message.content)
      showCopied.value = true
      setTimeout(() => {
        showCopied.value = false
      }, 1500)
    } catch {
      // Clipboard API may fail in non-secure contexts
    }
  }

  async function handleRating(value: 'positive' | 'negative') {
    if (rating.value === value) {
      rating.value = null
      return
    }

    rating.value = value

    try {
      await FeedbackService.feedbackCreate({
        requestBody: {
          rating: value === 'positive' ? '5.0' : '0.0',
          feature: FeatureEnum.AI_CHAT,
          url: window.location.href,
          related_data: {
            ai_chat: {
              ai_chat_session_uuid: props.sessionUuid,
              ai_chat_session_message_uuid: props.message.raw?.uuid
            }
          }
        }
      })
    } catch (error) {
      console.error('Failed to submit rating:', error)
      rating.value = null
    }
  }

  function handleDownload() {
    const blob = new Blob([props.message.content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ai-response-${new Date().toISOString().slice(0, 10)}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    morePopoverOpen.value = false
  }

  function handleSaveSnippet() {
    morePopoverOpen.value = false
    showSnippetDialog.value = true
  }
</script>

<template>
  <div class="flex flex-col items-start gap-2 self-stretch">
    <!-- Waiting for first token — animated wave -->
    <ChatStreamingLoader
      v-if="message.isStreaming && !message.content"
      :label="streamingLabel"
    />

    <!-- Message content (markdown rendered) -->
    <div
      v-else
      class="assistant-message-content self-stretch text-sm font-normal leading-5 text-gray-800 font-inter"
      v-html="renderedHtml"
    />

    <!-- Streaming cursor -->
    <span
      v-if="message.isStreaming && message.content"
      class="ml-0.5 inline-block h-3.5 w-1 animate-pulse rounded-sm bg-purple-400"
    />

    <!-- Action bar — hidden while streaming -->
    <div
      v-if="!message.isStreaming"
      class="inline-flex items-start gap-2"
    >
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          :class="[
            'flex h-8 items-center justify-center rounded-md p-1.5 transition-colors',
            rating === 'positive'
              ? 'bg-purple-50 text-purple-600'
              : 'text-gray-500 hover:text-purple-600'
          ]"
          :aria-label="t('thumbsUp')"
          @click="handleRating('positive')"
        >
          <ThumbsUp
            class="h-5 w-5"
            :stroke-width="1.5"
          />
        </button>
        <button
          type="button"
          :class="[
            'flex h-8 items-center justify-center rounded-md p-1.5 transition-colors',
            rating === 'negative'
              ? 'bg-purple-50 text-purple-600'
              : 'text-gray-500 hover:text-purple-600'
          ]"
          :aria-label="t('thumbsDown')"
          @click="handleRating('negative')"
        >
          <ThumbsDown
            class="h-5 w-5"
            :stroke-width="1.5"
          />
        </button>
      </div>
      <div class="flex items-center">
        <button
          type="button"
          class="flex h-8 items-center justify-center rounded-md p-1.5 text-gray-500 transition-colors
            hover:text-purple-600"
          :aria-label="showCopied ? t('copied') : t('copy')"
          @click="handleCopy"
        >
          <transition
            name="fade"
            mode="out-in"
          >
            <Check
              v-if="showCopied"
              key="check"
              class="h-5 w-5 text-purple-600"
              :stroke-width="1.5"
            />
            <Copy
              v-else
              key="copy"
              class="h-5 w-5"
              :stroke-width="1.5"
            />
          </transition>
        </button>
        <Popover v-model:open="morePopoverOpen">
          <PopoverTrigger as-child>
            <button
              type="button"
              class="flex h-8 items-center justify-center rounded-md p-1.5 text-gray-500 transition-colors
                hover:text-purple-600"
              :aria-label="t('moreOptions')"
            >
              <MoreHorizontal
                class="h-5 w-5"
                :stroke-width="1.5"
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            :side-offset="4"
            class="w-72 rounded-xl p-0 shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)]"
          >
            <button
              type="button"
              class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-sm font-normal text-gray-800
                hover:bg-gray-50"
              @click="handleSaveSnippet"
            >
              <Bookmark
                class="h-5 w-5"
                :stroke-width="1.25"
              />
              {{ t('saveSnippet') }}
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2.5 p-4 text-sm font-normal text-gray-800 hover:bg-gray-50"
              @click="handleDownload"
            >
              <ArrowDownToLine
                class="h-5 w-5"
                :stroke-width="1.25"
              />
              {{ t('download') }}
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </div>

    <!-- Preference suggestion (shown below the action bar when present) -->
    <SidePanelPreferenceSuggestion
      v-if="preferenceSuggestion && !message.isStreaming"
      :suggestion="preferenceSuggestion"
      @view-in-library="emit('openPreferencesDialog')"
    />

    <!-- Save Snippet Dialog -->
    <SaveSnippetDialog
      v-if="showSnippetDialog"
      :content="message.content"
      @close="showSnippetDialog = false"
    />
  </div>
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  /* Markdown prose styles — scoped to assistant messages only */
  .assistant-message-content :deep(p) {
    margin: 0.5em 0;
  }
  .assistant-message-content :deep(p:first-child) {
    margin-top: 0;
  }
  .assistant-message-content :deep(p:last-child) {
    margin-bottom: 0;
  }
  .assistant-message-content :deep(ul),
  .assistant-message-content :deep(ol) {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }
  .assistant-message-content :deep(li) {
    margin: 0.25em 0;
  }
  .assistant-message-content :deep(code) {
    font-size: 0.85em;
    padding: 0.15em 0.35em;
    border-radius: 4px;
    background-color: #f3f4f6;
    font-family: 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
  }
  .assistant-message-content :deep(pre) {
    margin: 0.75em 0;
    padding: 0.75em 1em;
    border-radius: 8px;
    background-color: #1f2937;
    overflow-x: auto;
  }
  .assistant-message-content :deep(pre code) {
    background-color: transparent;
    padding: 0;
    color: #e5e7eb;
    font-size: 0.8em;
  }
  .assistant-message-content :deep(blockquote) {
    margin: 0.5em 0;
    padding-left: 1em;
    border-left: 3px solid #d1d5db;
    color: #6b7280;
  }
  .assistant-message-content :deep(strong) {
    font-weight: 600;
  }
  .assistant-message-content :deep(h1),
  .assistant-message-content :deep(h2),
  .assistant-message-content :deep(h3),
  .assistant-message-content :deep(h4) {
    font-weight: 600;
    margin: 0.75em 0 0.25em;
    line-height: 1.3;
  }
  .assistant-message-content :deep(h1) {
    font-size: 1.25em;
  }
  .assistant-message-content :deep(h2) {
    font-size: 1.15em;
  }
  .assistant-message-content :deep(h3) {
    font-size: 1.05em;
  }
  .assistant-message-content :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75em 0;
    font-size: 0.85em;
  }
  .assistant-message-content :deep(th),
  .assistant-message-content :deep(td) {
    border: 1px solid #e5e7eb;
    padding: 0.4em 0.6em;
    text-align: left;
  }
  .assistant-message-content :deep(th) {
    background-color: #f9fafb;
    font-weight: 600;
  }
  .assistant-message-content :deep(hr) {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 1em 0;
  }
</style>
